/**
 * Что экран оформления вправе предложить: по подписке и по сумме заказа.
 *
 * Правила два, и оба про одно — не показывать кнопку, которая гарантированно
 * даёт отказ.
 *
 * **Первое: чем можно платить при уже заведённой подписке.** Оформление
 * отбивается, когда у организации есть подписка со статусом, отличным от
 * `expired`, — кроме `pending`. `pending` — это незакрытая первая оплата, и её
 * сервер ПРОДОЛЖАЕТ: и картой (`billing/services/checkout.py`), и счётом
 * (`billing/services/documents.py`).
 *
 * Раньше здесь стояло исключение: при `pending` счёт отключался, потому что
 * сервер отбивал его 409 без разбора статуса. Это и был тупик, из-за которого
 * появился этот файл: собственный счёт оставляет подписку в `pending`, то есть
 * первый же выставленный счёт делал второй невозможным навсегда, и опечатку в
 * ИНН исправить было нечем. Сервер асимметрию убрал — перевыставление заменяет
 * документ, старый аннулируется, вторая подписка не заводится, — и экран обязан
 * перестать запирать этот путь. Отдельного правила «счёт» больше нет:
 * оформление либо открыто целиком, либо закрыто целиком.
 *
 * **Второе: имеет ли смысл предлагать карту на эту сумму.** У ЮKassa потолок
 * разового платежа картой — 350 000 ₽, и `POST /v3/payments` сумму против него
 * НЕ проверяет: платёж создаётся, отдаёт ссылку и умирает уже на платёжной
 * странице с «Платёж не прошёл» без объяснения. Приёмка получила ровно это на
 * тарифе «Партнёр» за год (990 000 ₽). Порог приходит с сервера полем
 * `card_payment_max_kopecks` витрины: он настраивается окружением, и вторая
 * копия в коде экрана разъехалась бы с ним молча.
 */

/** Статусы подписки из `catalog.subscription_status` (миграция 0009). */
export type SubscriptionStatus =
  'pending' | 'active' | 'past_due' | 'cancelled' | 'expired';

/** Что экран делает с подпиской, которая уже есть у организации. */
export interface CheckoutGate {
  /** Форма не показывается вовсе: оформлять нечего, подписка действует. */
  occupied: boolean;
  /** Можно ли предлагать оплату по счёту. */
  invoice: boolean;
}

/**
 * Решение по статусу подписки, которую вернул `GET /billing/portal`.
 *
 * `null`/`undefined` — подписки нет вовсе: доступно и то и другое.
 *
 * Неизвестный статус трактуется как занято. Появится в контракте шестой — экран
 * промолчит и не пустит к оплате, вместо того чтобы предложить путь, которого
 * сервер не знает.
 */
export function checkoutGate(status: string | null | undefined): CheckoutGate {
  const free =
    status === null ||
    status === undefined ||
    status === 'expired' ||
    // Незакрытая первая оплата: сервер продолжает её обоими способами.
    status === 'pending';
  return { occupied: !free, invoice: free };
}

/** Чем платят. Путей два, и оба ведут к одной подписке. */
export type Method = 'card' | 'invoice';

/**
 * Проходит ли заказ картой.
 *
 * Сумма неизвестна ровно в одном случае — тариф продаётся по договору и цены в
 * витрине нет. Тогда карта не запрещается: до платежа дело всё равно не дойдёт,
 * такой тариф отбивается сервером отдельной причиной.
 */
export function cardAllowed(
  amountKopecks: number | null | undefined,
  limitKopecks: number | null | undefined,
): boolean {
  if (typeof amountKopecks !== 'number' || typeof limitKopecks !== 'number') return true;
  return amountKopecks <= limitKopecks;
}

/**
 * Чем платят НА САМОМ ДЕЛЕ, а не что выбрано в разметке.
 *
 * Выбор приходит из адреса (`?method=invoice` — ссылкой из письма, кнопкой с
 * экрана тарифа, закладкой) и живёт своим состоянием. Когда путь недоступен,
 * этот выбор обязан не действовать целиком — и на форме реквизитов, и в подписи
 * полосы оплаты, и в отправке. Ровно одна функция на все три места: разойтись
 * они могут только вместе.
 *
 * Порядок проверок важен. Недоступный счёт уводит на карту, недоступная карта —
 * на счёт; оба недоступны быть не могут: счёт закрыт только вместе со всей
 * формой, а на сумму, которую не берёт карта, счёт как раз и выставляют.
 */
export function effectiveMethod(
  choice: Method,
  gate: CheckoutGate,
  card: boolean = true,
): Method {
  if (!gate.invoice) return 'card';
  if (!card) return 'invoice';
  return choice;
}

/**
 * Почему счёта нет, словами для человека.
 *
 * Остаётся ради неизвестного статуса подписки: форма при нём не открывается
 * вовсе, но если правило когда-нибудь разойдётся, отказ обязан называть причину
 * и следующий шаг, а не состояние.
 */
export const INVOICE_HELD_REASON =
  'Сейчас у вас есть незавершённый платёж. Продолжите или отмените его на экране «Тариф» — там же лежат уже выставленные счета. После этого можно выставить новый.';

/**
 * Почему карты нет, словами для человека.
 *
 * Причина названа как ограничение платёжной системы, а не как наш отказ: это
 * правда, и она объясняет, почему у соседнего тарифа карта есть. Следующий шаг
 * назван сразу — счёт, и он уже выбран за человека.
 */
export function cardHeldReason(limitKopecks: number): string {
  // Разряды разбиваются вручную, а не `toLocaleString('ru-RU')`: тот ставит
  // неразрывный пробел в одних средах и обычный в других, и текст перестаёт
  // совпадать сам с собой между браузером, проверкой и снимком экрана.
  const limit = String(Math.floor(limitKopecks / 100)).replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ' ',
  );
  return `Платёж картой в ЮKassa ограничен суммой ${limit} ₽, а этот заказ дороже. Такие подписки оплачиваются по счёту на юридическое лицо — реквизиты ниже, счёт выставится сразу.`;
}
